import type React from "react";
import { useTranslation } from "react-i18next";

import { useModal } from "hooks/useModal";

import { ModalRenderType } from "components/Modal/Modal.model";

import { Heading2 } from "styles/elements/Headings";

import { EncircledIcon } from "components/EncircledIcon";
import { CssFlex } from "styles/helpers/layout";

import {
  CancelButton,
  NextButton,
  PrevButton,
  StyledButtonContainer,
  StyledHorizontalLine,
  StyledModal,
  StyledStep,
  StyledStepSeparator,
} from "./MultiStepModal.styled";

import { Fragment, useEffect, useState } from "react";
import { type MultiStepModalProps, StepStatus } from "./MultiStepModal.types";

export const MultiStepModal: React.FC<MultiStepModalProps> = ({
  id: modalId,
  className,
  dataCy,
  title,
  steps,
  onClose,
  autoCloseOnLastNextButtonClick = true,
}) => {
  const { t } = useTranslation("common");
  const [activeStepIndex, setActiveStepIndex] = useState(0);
  const { closeModal, isModalOpen } = useModal();

  const isModalCurrentlyOpen = isModalOpen(modalId);

  // Reset active step everytime the modal closes
  useEffect(() => {
    if (!isModalCurrentlyOpen) {
      setActiveStepIndex(0);
    }
  }, [isModalCurrentlyOpen]);

  const activeStep = steps[activeStepIndex];

  const triggerCloseModal = () => {
    closeModal();
    onClose?.();
  };

  const onCancelButtonClick = () => triggerCloseModal();

  const onPrevButtonClick = () => {
    if (activeStepIndex > 0) {
      setActiveStepIndex(activeStepIndex - 1);
    } else {
      triggerCloseModal();
    }
  };

  const onNextButtonClick = (customHandler?: () => void) => {
    if (customHandler) {
      customHandler();
    }

    // By default, always move to next step (or close modal if there aren't more)
    if (activeStepIndex < steps.length - 1) {
      setActiveStepIndex(activeStepIndex + 1);
    } else if (autoCloseOnLastNextButtonClick) {
      triggerCloseModal();
    }
  };

  return (
    <StyledModal
      className={className}
      dataCy={dataCy || modalId}
      isOpen={isModalCurrentlyOpen}
      renderToElementType={ModalRenderType.REACT_PORTAL}
      onClose={triggerCloseModal}
    >
      <CssFlex flexDirection="column" gap={0.5} padding="2rem" paddingBottom="1rem">
        <Heading2>{title}</Heading2>
        <CssFlex flexDirection="row" gap={1} alignItems="center" flexWrap="wrap">
          {steps.map((step, index) => {
            const stepStatus =
              index === activeStepIndex
                ? StepStatus.Active
                : index < activeStepIndex
                  ? StepStatus.Complete
                  : StepStatus.Upcoming;
            const iconVariant =
              stepStatus === StepStatus.Active
                ? "primary"
                : stepStatus === StepStatus.Complete
                  ? "secondary"
                  : "default";

            return (
              <Fragment key={step.id}>
                {/* Render separator except on first item */}
                {index > 0 && <StyledStepSeparator iconName="fal fa-long-arrow-right" />}
                <StyledStep $status={stepStatus}>
                  <EncircledIcon iconName={step.iconName} variant={iconVariant} size="small" />
                  {step.label}
                </StyledStep>
              </Fragment>
            );
          })}
        </CssFlex>
      </CssFlex>
      <StyledHorizontalLine />
      <CssFlex flexDirection="column" gap={2} padding="2rem" paddingTop="1rem">
        {/* -- Current step content -- */}
        <section data-cy={activeStep.id}>{activeStep.content}</section>

        {/* -- Button container -- */}
        <StyledButtonContainer>
          {!activeStep.hideCancelButton && (
            <CancelButton dataCy="cancel-button" onClick={onCancelButtonClick}>
              {activeStep.cancelButtonLabel || t("common:multiStepModal.cancelButton")}
            </CancelButton>
          )}
          {!activeStep.hidePrevButton && activeStepIndex > 0 && (
            <PrevButton dataCy="prev-step-button" onClick={onPrevButtonClick} variant="secondary">
              {activeStep.prevButtonLabel || t("common:multiStepModal.prevButton")}
            </PrevButton>
          )}
          <NextButton
            dataCy="next-step-button"
            disabled={activeStep.disableNextButton}
            onClick={() => onNextButtonClick(activeStep.onNextButtonClick)}
            variant="primary"
          >
            {activeStep.nextButtonLabel || t("common:multiStepModal.nextButton")}
          </NextButton>
        </StyledButtonContainer>
      </CssFlex>
    </StyledModal>
  );
};
